import DOMPurify from "dompurify";
import linkifyHtml from "linkify-html";

// Matches a real HTML element tag (e.g. <div>, <br/>, <a href="...">), but NOT
// angle-bracketed plain-text URLs like <https://...> that Gmail uses — for
// those nothing valid follows the scheme word (no `>`, space, or `/`)
const HTML_TAG_REGEX = /<\/?[a-z][a-z0-9]*(\s[^>]*)?\/?>/i;

// Gmail represents a link in a plain-text body as "label\n<URL>" — e.g. a large
// file is inlined as a filename line followed by its Drive URL in angle
// brackets. We anchor on the mandatory <URL> token (rather than a floating,
// overlapping label prefix) so the scan stays linear and can't be driven into
// catastrophic backtracking by a hostile body. Also matches a bare "<URL>".
const ANGLE_URL_REGEX = /<(https?:\/\/[^>\s]+)>/gi;

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Builds safe HTML from a plain-text body: turns the "label\n<URL>" convention
// into anchors (label as link text), escapes everything else, and keeps line
// breaks
const plainTextToHtml = (text: string): string => {
  let html = "";
  let lastIndex = 0;

  Array.from(text.matchAll(ANGLE_URL_REGEX)).forEach((match) => {
    const [full, url] = match;
    const start = match.index ?? 0;

    const between = text.slice(lastIndex, start);

    // If the text right before the <URL> is a single non-empty line ending in a
    // newline, use it as the link text and drop it from the escaped output.
    let prefix = between;
    let linkText = url;

    const newlineMatch = between.match(/\r?\n$/);
    if (newlineMatch) {
      const beforeNewline = between.slice(0, -newlineMatch[0].length);
      const lastBreak = beforeNewline.lastIndexOf("\n");
      const labelLine = beforeNewline.slice(lastBreak + 1);

      // a label must be a single non-empty line with no angle brackets
      if (labelLine.trim() && !/[<>]/.test(labelLine)) {
        linkText = labelLine.trim();
        prefix = beforeNewline.slice(0, lastBreak + 1);
      }
    }

    html += escapeHtml(prefix);
    html += `<a href="${escapeHtml(
      url
    )}" target="_blank" rel="noopener noreferrer">${escapeHtml(linkText)}</a>`;

    lastIndex = start + full.length;
  });

  html += escapeHtml(text.slice(lastIndex));

  return html.replace(/\r?\n/g, "<br/>");
};

/**
 * Prepares an email body for rendering inside a chat bubble:
 * - HTML bodies are sanitized as-is (they carry their own formatting)
 * - plain-text bodies keep their line breaks and have Gmail's "label\n<URL>"
 *   links (e.g. large-file Drive attachments) rendered as anchors
 * - bare URLs are linkified into anchors
 */
export const emailBodyToHtml = (text: string): string => {
  const html = HTML_TAG_REGEX.test(text) ? text : plainTextToHtml(text);

  return linkifyHtml(DOMPurify.sanitize(html, { ADD_ATTR: ["target"] }), {
    defaultProtocol: "https",
    target: "_blank",
    rel: "noopener noreferrer",
  });
};
